<template>
<w-sliding-popup title="과거 Flow 이력" ref="flowHistPopup" @close="onClosePopup">
  <div slot="content">
    <!-- 선택 정보 -->
    <div class="title-bar title-h4">선택 정보</div>
    <el-row class="mr-b30" :gutter="20">
      <el-col :span="12">
        <div class="list form-list">
          <el-form :model="histData" label-position="left">
            <el-form-item label="인터페이스" :label-width="formLabelWidth"><span>{{ histData.netinterface }}</span></el-form-item>
            <el-form-item label="특정 IP" :label-width="formLabelWidth"><span>{{ histData.ipaddress }}</span></el-form-item>
            <el-form-item label="특정 프로토콜" :label-width="formLabelWidth"><span>{{ histData.protocol }}</span></el-form-item>
          </el-form>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="list form-list">
          <el-form :model="histData" label-position="left">
            <el-form-item label="인터페이스 방향" :label-width="formLabelWidth"><span>{{ histData.direction }}</span></el-form-item>
            <el-form-item label="Conversation" :label-width="formLabelWidth"><span>{{ histData.conversation }}</span></el-form-item>
            <el-form-item label="특정 서비스포트" :label-width="formLabelWidth"><span>{{ histData.port }}</span></el-form-item>
          </el-form>
        </div>
      </el-col>
    </el-row>
    <!-- 과거 이력 그래프 -->
    <el-row class="title-bar" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h4 mr-r10">과거 이력 그래프</div>
        <el-select type="medium" style="width: 100px;" v-model="unitType" @change="onChangeUnit">
          <el-option v-for="unit in unitList" :key="unit.value" :label="unit.label" :value="unit.value" />
        </el-select>
      </div>
      <div class="vertical-center-box">
        <i class="far fa-clock mr-r2" />
        <span class="mr-r10">{{ flowDateLabel }}</span>
        <el-button class="mr-r4" size="mini" @click="onClickLeftButton"><i class="fas fa-angle-left" /></el-button>
        <el-button size="mini" @click="onClickRightButton" :disabled="disabledRightBtn"><i class="fas fa-angle-right" /></el-button>
      </div>
    </el-row>
    <highstock class="mr-b30" ref="histChart" :options="options" />
    <el-table-wrapper ref="trafficTable" v-loading="loading" :max-height="310" :columns="tableColumns" :data="flowHistData" :total="total" :show-custom-header="false" header-align="center" :pagination="true" remote @condition-change="loadFlowHistData" />
  </div>
</w-sliding-popup>
</template>

<script>
import { mapState } from 'vuex';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import { isEmpty } from '@/utils/basic';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { convertValuByUnit } from '@/utils/number';
import MsgUtil from '@/utils/message';
import { chartColorSet } from '@/constants/chart-options';
import FlowAPI from '@/api/performance/flow-analysis/flow-analysis';

const initHistData = {
  netinterface: EMPTY_TEXT,
  ipaddress: EMPTY_TEXT,
  protocol: EMPTY_TEXT,
  direction: EMPTY_TEXT,
  conversation: EMPTY_TEXT,
  port: EMPTY_TEXT,
};

const options = {
  chart: {
    type: 'line',
    height: 190,
    backgroundColor: 'transparent',
    spacingTop: 5,
    spacingBottom: 0,
    zoomType: 'x',
  },
  title: false,
  credits: false,
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  xAxis: {
    type: 'datetime',
    minRange: 5 * 60 * 1000,
    labels: {
      format: '{value: %Y-%m-%d %H:%M}',
      style: {
        color: '$chinagray',
        fontSize: '9px',
      },
    },
    crosshair: true,
  },
  yAxis: {
    minTickInterval: 0,
    startOnTick: false,
    opposite: false,
  },
  navigator: {
    height: 20,
  },
  rangeSelector: false,
  scrollbar: false,
  plotOptions: {
    series: {
      animation: false,
      lineWidth: 1.8,
      marker: {
        radius: 2,
      },
    },
  },
  legend: false,
  series: [],
  colors: chartColorSet,
};
export default {
  name: 'WFlowHistPopup',
  props: {
    smartTraceFilterList: Array,
    flowParams: Object,
  },
  data() {
    return {
      visible: false,
      histData: clone(initHistData),
      formLabelWidth: '120px',
      options: cloneDeep(options),
      unitType: 'octets',
      unitList: [{ label: 'Bytes', value: 'octets' }, { label: 'Packets', value: 'packets' }, { label: 'bps', value: 'bps' }, { label: 'pps', value: 'pps' }],
      flowHistParams: {},
      tableColumns: [
        { prop: 'timestamp', label: '발생일시', width: 150, formatter: this.getDateToString },
        { prop: 'deviceName', label: '장비', width: 150, formatter: this.formatterForDevice },
        { prop: 'inputName', label: '인터페이스(In)', width: 150, formatter: this.formatterForInput },
        { prop: 'outputName', label: '인터페이스(Out)', width: 150, formatter: this.formatterForOutput },
        { prop: 'srcIpv4', label: 'IP(In)', width: 120, formatter: this.getValueAfterCheckEmpty },
        { prop: 'dstIpv4', label: 'IP(Out)', width: 120, formatter: this.getValueAfterCheckEmpty },
        { prop: 'srcPort', label: '포트(In)', formatter: this.getValueAfterCheckEmpty },
        { prop: 'dstPort', label: '포트(Out)', formatter: this.getValueAfterCheckEmpty },
        { prop: 'protocol', label: '프로토콜', formatter: this.getValueAfterCheckEmpty },
        { prop: 'octets', label: 'Bytes', formatter: this.convertValuByUnit },
        { prop: 'packets', label: 'Packets', formatter: this.convertValuByUnit },
      ],
      // pagination: {
      //   pageSize: 10,
      //   pageSizes: [10, 20, 50, 100],
      //   layout: 'sizes, prev, pager, next',
      //   currentPage: 1,
      //   total: 0,
      //   onSizeChange: this.onChangePageSize,
      //   onCurrentChange: this.onChangeCurrentPage,
      //   firstLoad: false,
      // },
      flowHistData: [],
      total: 0,
      startDateTime: null,
      endDateTime: null,
      loading: false,
    };
  },
  computed: {
    flowDateLabel() {
      const analysisDateData = this.analysisDateData;
      if (isEmpty(analysisDateData) || isNil(analysisDateData) || isNil(analysisDateData.data)) {
        return null;
      }

      const startDateTime = isEmpty(this.startDateTime) ? analysisDateData.data.startValue : this.startDateTime;
      const endDateTime = isEmpty(this.endDateTime) ? analysisDateData.data.endValue : this.endDateTime;

      return `${getDateToString(startDateTime, 'YYYY-MM-DD HH:mm')} ~ ${getDateToString(endDateTime, 'YYYY-MM-DD HH:mm')}`;
    },
    disabledRightBtn() {
      const startDateTime = this.startDateTime;
      const endDateTime = this.endDateTime;
      const today = new Date().getTime();

      return startDateTime > today || endDateTime > today;
    },
    ...mapState({ analysisDateData: (state) => state.performance.analysisDateData }),
  },
  watch: {
    /**
     * 선택 정보 데이터 바인딩
     * @param  {[type]} visible [description]
     * @return {[type]}         [description]
     * @author jda
     */
    visible(visible) {
      if (visible) {
        const flowParams = cloneDeep(this.flowParams);
        const analysisFilter = flowParams.requestParamVO.analysisFilter[flowParams.analysisFilterName];
        const smartTraceFilterList = this.smartTraceFilterList;
        const histData = this.histData;

        // ip의 dstIp와 conversation의 dstIp가 겹치므로 flowParams.analysisFilterName로는 비교하지 않는다.
        const findTraceData = find(smartTraceFilterList, { cardType: flowParams.analysisType });
        if (isEmpty(findTraceData)) {
          // 인터페이스별 분석일 경우 방향도 바인딩한다.
          if (flowParams.analysisType === 'netinterface') {
            histData.direction = flowParams.direction === 'input' ? 'IN' : 'OUT';
          }
          histData[flowParams.analysisType] = this.getFilterName(analysisFilter);
        }

        const smartTraceFilter = {};
        smartTraceFilterList.forEach((traceData) => {
          const type = traceData.type;
          // 장비별 분석에서 인터페이스 카드의 선택 항목이 있다면 방향도 바인딩한다.
          if (traceData.cardType === 'netinterface') {
            histData.direction = type.toLowerCase().indexOf('input') > -1 ? 'IN' : 'OUT';
          }
          let typeName = null;
          if (type.indexOf('src') > -1) {
            typeName = 'IN';
          } else if (type.indexOf('dst') > -1) {
            typeName = 'OUT';
          }

          histData[traceData.cardType] = isEmpty(typeName) ? traceData.name : `${traceData.name} (${typeName})`;
          smartTraceFilter[traceData.type] = traceData.itemId;
        });
        Object.assign(flowParams.requestParamVO, { smartTraceFilter });
        this.flowHistParams = Object.assign({}, flowParams);
        this.setDateTimeParams();

        this.firstLoad = true;
        // 과거 이력 그래프 데이터 조회
        this.loadFlowHistChart();
        if (this.$refs.trafficTable) {
          this.$refs.trafficTable.setCurrentPage(1);
        } else {
          this.$nextTick(() => {
            this.$refs.trafficTable.setCurrentPage(1);
          });
        }
      } else {
        this.histData = clone(initHistData);
        this.options.series = [];
        this.flowHistData = [];
      }
    },
  },
  mounted() {
    // tooltip은 YYYY-MM-DD HH:mm 형식으로 표현
    this.options.tooltip = {
      formatter() {
        return `${getDateToString(this.x, 'YYYY-MM-DD HH:mm')}<br> 트래픽 사용량 : ${convertValuByUnit(this.y, null, true, false, true)}`;
      },
    };
  },
  methods: {
    /**
     * Flow 과거이력 조회 팝업창을 띄운다.
     * @return {[type]} [description]
     * @author jda
     */
    open() {
      this.visible = true;
      this.$refs.flowHistPopup.visible = true;
    },
    /**
     * Flow 과거이력 조회 팝업창을 닫는다.
     * @return {[type]} [description]
     * @author jda
     */
    onClosePopup() {
      this.visible = false;
      this.$refs.flowHistPopup.visible = false;
    },
    /**
     * [getFilterName description]
     * @param  {Boolean} analysisFilter [description]
     * @return {[type]}                 [description]
     * @author jda
     */
    getFilterName(analysisFilter) {
      if (analysisFilter.length < 1) {
        return EMPTY_TEXT;
      }

      const analysisFilterMap = this.flowParams.analysisFilterMap;
      let value;
      if (analysisFilter.length > 1) {
        value = `${analysisFilterMap[analysisFilter[0]]}외 ${analysisFilter.length - 1}개`;
      } else {
        value = analysisFilterMap[analysisFilter[0]];
      }
      return value;
    },
    /**
     * 과거 이력 그래프 데이터를 조회한다.
     * @return {[type]} [description]
     * @author jda
     */
    loadFlowHistChart() {
      const requestParamVO = this.flowHistParams.requestParamVO;
      FlowAPI.getFlowHistChartData(requestParamVO)
        .then((response) => {
          if (response.data.success) {
            this.options.series = response.data.data;
            if (this.firstLoad) {
              // 최초 팝업창이 띄어졌을 때 Flow 분석 추이차트에서 필터링한 datetime을 바인딩한다.
              this.firstLoad = false;
              this.$nextTick(() => {
                const flowParams = this.flowParams.requestParamVO;
                const chart = this.$refs.histChart.chart;
                if (!isEmpty(chart)) {
                  chart.xAxis[0].setExtremes(flowParams.startDateTime, flowParams.endDateTime);
                }
              });
            }
          } else {
            this.options.series = [];
            this.$message(MsgUtil.getCustomResultMsgData('과거 이력 그래프 데이터를 조회하지 못하였습니다.', false));
          }
        })
        .catch(() => {
          this.$message(MsgUtil.getCustomResultMsgData('과거 이력 그래프 데이터를 조회하지 못하였습니다.', false));
        });
    },
    /**
     * 단위를 바꿀 때마다 과거 이력 그래프 데이터를 다시 조회힌다.
     * @param  {[type]} unitType [description]
     * @return {[type]}          [description]
     * @author jda
     */
    onChangeUnit(unitType) {
      const flowHistParams = this.flowHistParams;
      Object.assign(flowHistParams.requestParamVO, { meter: unitType });

      // 페이지 index 리셋
      // this.pagination.currentPage = 1;

      this.loadFlowHistChart();
      this.$refs.trafficTable.setCurrentPage(1);
    },
    /**
     * 날짜를 YYYY-MM-DD HH:mm:ss형식으로 변환한다.
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    getDateToString(row, column, value) {
      return getDateToString(value);
    },
    /**
     * 빈 데이터 확인
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    getValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * Bytes와 Packets는 단위환산한다.
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertValuByUnit(row, column, value) {
      let unit = null;
      if (column.property === 'octets') {
        unit = 'Byte';
      } else if (column.property === 'packets') {
        unit = 'count';
      }
      return convertValuByUnit(value, unit, true, false, true);
    },
    loadFlowHistData() {
      const params = clone(this.flowHistParams.requestParamVO);
      const pagination = this.$refs.trafficTable.getPagination();
      Object.assign(params, {
        pageSize: pagination.pageSize,
        pageIndex: pagination.currentPage,
      });
      this.loading = true;
      FlowAPI.getFlowHistData(params)
        .then((response) => {
          const result = response.data;
          if (response.data.success) {
            this.flowHistData = result.data;
            this.total = result.total;
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '트래픽', false));
          }
        })
        .catch(() => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '트래픽', false));
        }).finally(() => {
          this.loading = false;
        });
    },
    /**
     * page size변경
     * @param  {[type]} pageSize [description]
     * @return {[type]}          [description]
     * @author jda
     */
    // onChangePageSize(pageSize) {
    //   this.pagination.currentPage = 1;
    //   this.pagination.pageSize = pageSize;
    //   this.loadFlowHistData();
    // },
    /**
     * 현재 페이지 변경
     * @return {[type]} [description]
     * @author jda
     */
    // onChangeCurrentPage(currentPage) {
    //   this.pagination.currentPage = currentPage;
    //   this.loadFlowHistData();
    // },
    /**
     * flow이력을 조회할 date param
     * @author jda
     */
    setDateTimeParams() {
      const analysisDateParams = this.analysisDateData.params;
      Object.assign(this.flowHistParams.requestParamVO, { startDateTime: analysisDateParams.startDatetime, endDateTime: analysisDateParams.endDatetime });
    },
    /**
     * 과거이력그래프 [<]버튼 클릭시 Flow분석의 기간필터에서 설정한 시간 간격만큼 이전시간으로 이동한다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickLeftButton() {
      const params = clone(this.flowHistParams.requestParamVO);
      const diffTime = params.endDateTime - params.startDateTime;
      const startDateTime = params.startDateTime - diffTime;
      this.startDateTime = startDateTime;

      const endDateTime = params.endDateTime - diffTime;
      this.endDateTime = endDateTime;

      Object.assign(params, { startDateTime, endDateTime });
      this.flowHistParams = Object.assign(this.flowHistParams, { requestParamVO: params });

      // 페이지 index 리셋
      // this.pagination.currentPage = 1;

      this.loadFlowHistChart();
      this.$refs.trafficTable.setCurrentPage(1); // this.loadFlowHistData()
    },
    /**
     * 과거이력그래프 [>]버튼 클릭시 Flow분석의 기간필터에서 설정한 시간 간격만큼 이후시간으로 이동한다.
     * 미래 시간이 있을 경우 버튼은 비활성화 된다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickRightButton() {
      const params = clone(this.flowHistParams.requestParamVO);
      const diffTime = params.endDateTime - params.startDateTime;
      const startDateTime = params.startDateTime + diffTime;
      this.startDateTime = startDateTime;

      const endDateTime = params.endDateTime + diffTime;
      this.endDateTime = endDateTime;

      Object.assign(params, { startDateTime, endDateTime });
      this.flowHistParams = Object.assign(this.flowHistParams, { requestParamVO: params });

      // 페이지 index 리셋
      this.loadFlowHistChart();
      this.$refs.trafficTable.setCurrentPage(1);
    },
    /**
     * 장비 정보 formatter
     * @author Ju
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    formatterForDevice(row) {
      return isNil(row.deviceName) || row.deviceName === '' ? getValueAfterCheckEmpty(row.deviceIp) : row.deviceName;
    },
    /**
     * 인터페이스 (in) 정보 formatter
     * @author Ju
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    formatterForInput(row) {
      return isNil(row.inputName) || row.inputName === '' ? getValueAfterCheckEmpty(row.input) : row.inputName;
    },
    /**
     * 인터페이스 (out) 정보 formatter
     * @author Ju
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    formatterForOutput(row) {
      return isNil(row.outputName) || row.outputName === '' ? getValueAfterCheckEmpty(row.output) : row.outputName;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/FlowAnalysis/components/popup/FlowHistPopup.vue